import React, { Fragment, useReducer, useEffect } from "react";
import { toast } from "react-toastify";
import { useAuth0 } from "@auth0/auth0-react";
import * as Sentry from "@sentry/react";

import Checkbox from "@mui/material/Checkbox";

import ai from "../../helpers/axios";

import { useHistory, useParams } from "react-router-dom";
import Select from "react-select";
import FormControlLabel from "@mui/material/FormControlLabel";
import getApiErrorMessage from "../../helpers/get-api-error-message";

const EVOJETS_ORG_ID = "600c8d371f10c61723e64402";
const AD_ROLE_ID = "rol_pOQalM41JyTqKsF4";

// accountDirectorId is only ever consumed for Sales Assistant users, so only
// they require one on save (mirrors the create form). Super Admins and
// Dynamic Sales Assistants are no longer blocked on picking a bogus AD.
const AD_REQUIRED_ROLE_NAMES = ["Sales Assistant"];
const AD_OPTIONAL_ROLE_NAMES = ["Account Executive"];

// Cosmetic team color for Account Directors ("Red Team's Leads" page titles).
// Fixed palette — free text produced "red"/"RED" variants. "None" maps to ""
// which the server converts to null so Auth0 removes the key.
const TEAM_COLOR_OPTIONS = [
  { label: "None", value: "" },
  { label: "Red", value: "Red" },
  { label: "Blue", value: "Blue" },
  { label: "Green", value: "Green" },
  { label: "Orange", value: "Orange" },
  { label: "Purple", value: "Purple" },
];

const toastOptions = {
  position: "top-right",
  autoClose: 5000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
};

const reducer = (state, action) => {
  switch (action.type) {
    case "search": {
      return {
        ...state,
        query: action.query,
      };
    }

    case "fieldChanged": {
      return {
        ...state,
        [action.field]: action.value,
      };
    }

    case "dataDownloaded": {
      let selectedRole = { id: "", label: "", value: "" };
      if (action?.payload?.data?.user?.userRoles?.length > 0) {
        selectedRole = {
          id: action.payload.data.user.userRoles[0].id,
          name: action.payload.data.user.userRoles[0].name,
          description: action.payload.data.user.userRoles[0].description,
          value: action.payload.data.user.userRoles[0].id,
          label: action.payload.data.user.userRoles[0].name,
        };
      }
      let selectedAccountDirector = null;

      // Match against the AD list delivered with this payload — the user load
      // is sequenced after the AD list load, so relying on
      // state.availableAccountDirectors here raced and intermittently blanked
      // the AD select for users who already have one.
      const accountDirectors = action?.payload?.accountDirectors?.length
        ? action.payload.accountDirectors
        : state.availableAccountDirectors;

      if (
        action?.payload?.data?.user?.app_metadata?.accountDirectorId &&
        accountDirectors?.length
      ) {
        const matchingAD = accountDirectors.find(
          (ad) =>
            ad?.user_id ===
            action?.payload?.data?.user?.app_metadata?.accountDirectorId
        );

        if (matchingAD) {
          selectedAccountDirector = { ...matchingAD };
        }
      }

      return {
        ...state,
        user: action.payload.data.user,
        phone: action.payload.data.user.user_metadata.phoneNumber,
        email: action.payload.data.user.email,
        firstName: action.payload.data.user.name.split(" ")[0],
        lastName: action.payload.data.user.name.split(" ")[1],
        teamColor: action.payload.data.user.app_metadata?.teamColor,
        selectedOrganization: {
          label: action.payload.data.user.app_metadata.organizationName,
          value: action.payload.data.user.app_metadata.organizationId,
        },
        selectedRole,
        selectedAccountDirector,
        isSalesRep: action.payload.data.user.app_metadata.isSalesRep === true,
        isOps: action.payload.data.user.app_metadata.isOps,
        isAccounting: action.payload.data.user.app_metadata.isAccounting,
        isAccountingLedger: action.payload.data.user.app_metadata.isAccountingLedger,
        isAccountingPending: action.payload.data.user.app_metadata.isAccountingPending,
        isDutySales: action.payload.data.user.app_metadata.isDutySales,
        isDutyAdmin: action.payload.data.user.app_metadata.isDutyAdmin,
        isVerificationAdmin: action.payload.data.user.app_metadata.isVerificationAdmin,
        isCcApprover: action.payload.data.user.app_metadata.isCcApprover === true,
        isMarkSentAllowed: action.payload.data.user.app_metadata.isMarkSentAllowed === true,
        isHighDollarApprover: action.payload.data.user.app_metadata.isHighDollarApprover === true,
        isHighDollarApprover100k: action.payload.data.user.app_metadata.isHighDollarApprover100k === true,
        isMarkSentBlocked: action.payload.data.user.app_metadata.isMarkSentBlocked === true,
        // availableRoles: [
        //   ...action.payload.data.roles.map((role) => {
        //     return { ...role, label: role.name, value: role.id };
        //   }),
        // ],
      };
    }

    default:
      break;
  }
  return state;
};

const SingleUserContent = (props) => {
  const history = useHistory();
  const { id } = useParams();

  const initialState = {
    organizations: [],
    availableRoles: [],
    availableAccountDirectors: [],
    availableOutreachUsers: [],
    availableTeamMembers: [],
    selectedOrganization: {},
    selectedOutreachUser: {},
    selectedTeamMembers: [],
    phone: "",
    email: "",
    firstName: "",
    lastName: "",
    // password: "",
    teamColor: "",
    selectedAccountDirector: null,
    user: null,
    selectedRole: { id: "", value: "", label: "" },
    isSalesRep: false,
    isOps: false,
    isAccounting: false,
    isAccountingLedger: false,
    isAccountingPending: false,
    isDutySales: false,
    isDutyAdmin: false,
    isVerificationAdmin: false,
    isCcApprover: false,
    isMarkSentAllowed: false,
    isHighDollarApprover: false,
    isHighDollarApprover100k: false,
    isMarkSentBlocked: false,
    isSubmitting: false,
  };

  const { user, getAccessTokenSilently, loginWithRedirect } = useAuth0();

  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    document.title = "Edit User | evoJets HQ";
    getData();
  }, []);

  const getData = async () => {
    dispatch({ type: "dataBeganDownloading" });

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    // The user load depends on the AD and team-member lists (the reducer
    // matches the user's saved ids against them), so it runs only after every
    // list has settled and receives the lists directly.
    const [accountDirectorsResult, teamMembersResult] =
      await Promise.allSettled([
        loadAccountDirectors(token),
        loadTeamMembers(token),
        loadRoles(token),
        loadOutreachUsers(token),
        loadOrganizations(token),
      ]);

    const accountDirectors =
      accountDirectorsResult.status === "fulfilled"
        ? accountDirectorsResult.value || []
        : [];
    const teamMembers =
      teamMembersResult.status === "fulfilled"
        ? teamMembersResult.value || []
        : [];

    await loadUser(token, teamMembers, accountDirectors);
  };

  const loadUser = async (token, teamMembers = [], accountDirectors = []) => {
    let user = await ai
      .auth(token)
      .get(`/api/users/${id}`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    if (user) {
      let data = { ...user.data };

      if (
        teamMembers.length &&
        data?.user?.userRoles?.length &&
        data?.user?.userRoles[0]?.id === AD_ROLE_ID &&
        data?.user?.app_metadata?.myTeam?.length
      ) {
        let matched = teamMembers.filter((member) =>
          data?.user?.app_metadata?.myTeam.includes(member.user_id)
        );

        dispatch({
          type: "fieldChanged",
          field: "selectedTeamMembers",
          value: matched,
        });
      }

      dispatch({
        type: "dataDownloaded",
        payload: {
          data,
          accountDirectors,
        },
      });
    }
  };

  const loadRoles = async (token) => {
    let roles = await ai
      .auth(token)
      .get(`/api/users/availableRoles`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });
    if (
      roles &&
      roles.data &&
      roles.data.roles &&
      roles.data.roles.length > 0
    ) {
      dispatch({
        type: "fieldChanged",
        field: "availableRoles",
        value: roles.data.roles.map((role) => {
          return { ...role, label: role.name, value: role.id };
        }),
      });
    }
  };

  const loadAccountDirectors = async (token) => {
    let accountDirectors = await ai
      .auth(token)
      .get(`/api/users/roles/accountDirectors`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    if (accountDirectors?.data?.users?.length) {
      //account directors loaded
      accountDirectors = accountDirectors.data.users.map((ad) => {
        return {
          ...ad,
          label: ad?.name,
          value: ad?.user_id,
        };
      });

      dispatch({
        type: "fieldChanged",
        value: accountDirectors,
        field: "availableAccountDirectors",
      });

      return accountDirectors;
    }

    return [];
  };

  const loadTeamMembers = async (token) => {
    let teamMembers = await ai
      .auth(token)
      .get(`/api/users/roles/dynamicSales`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    if (teamMembers?.data?.users?.length) {
      //team members loaded
      teamMembers = teamMembers.data.users.map((user) => {
        return {
          ...user,
          label: user?.name,
          value: user?.user_id,
        };
      });

      dispatch({
        type: "fieldChanged",
        value: teamMembers,
        field: "availableTeamMembers",
      });

      return teamMembers;
    }

    return [];
  };

  const loadOutreachUsers = async (token) => {
    let outreachUsers = await ai
      .auth(token)
      .get(`/api/users/outreachUsers`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    if (outreachUsers?.data?.data?.length) {
      //account directors loaded
      outreachUsers = outreachUsers.data.data.map((user) => {
        return {
          label: user?.attributes?.name,
          value: user.id,
        };
      });

      dispatch({
        type: "fieldChanged",
        value: outreachUsers,
        field: "availableOutreachUsers",
      });
    }
  };

  const loadOrganizations = async (token) => {
    let organizations = await ai
      .auth(token)
      .get(`/api/organizations/list`)
      .catch((error) => {
        console.error(error);
        Sentry.captureException(error);
      });

    if (
      organizations &&
      organizations.data &&
      organizations.data.organizations &&
      organizations.data.organizations.length > 0
    ) {
      let organizationsFormatted = organizations.data.organizations.map(
        (organization) => {
          return {
            ...organization,
            label: organization.name,
            value: organization._id,
          };
        }
      );

      dispatch({
        type: "fieldChanged",
        field: "organizations",
        value: organizationsFormatted,
      });
    }
  };

  // Shared by the submit checks and the JSX field gates below so a required
  // field can never be hidden.
  const selectedRoleName = state.selectedRole?.name;
  const isEvoOrg = state.selectedOrganization?.value === EVOJETS_ORG_ID;
  const isAccountDirector = state.selectedRole?.id === AD_ROLE_ID;
  const accountDirectorRequired =
    isEvoOrg && AD_REQUIRED_ROLE_NAMES.includes(selectedRoleName);
  const showAccountDirectorField =
    accountDirectorRequired ||
    (isEvoOrg && AD_OPTIONAL_ROLE_NAMES.includes(selectedRoleName));

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (state.isSubmitting) {
      return;
    }

    let formData = {
      firstName: state.firstName,
      lastName: state.lastName,
      name: `${state.firstName} ${state.lastName}`,
      phone: state.phone,
      email: state.email,
      organizationId: state.selectedOrganization.value,
      // password: state.password,
      teamColor: state.teamColor,
      isSalesRep: state.isSalesRep,
      isOps: state.isOps,
      isAccounting: state.isAccounting,
      isAccountingLedger: state.isAccountingLedger,
      isAccountingPending: state.isAccountingPending,
      isDutySales: state.isDutySales,
      isDutyAdmin: state.isDutyAdmin,
      isVerificationAdmin: state.isVerificationAdmin,
      isCcApprover: state.isCcApprover,
      isMarkSentAllowed: state.isMarkSentAllowed,
      isHighDollarApprover: state.isHighDollarApprover,
      isHighDollarApprover100k: state.isHighDollarApprover100k,
      isMarkSentBlocked: state.isMarkSentBlocked,
    };

    if (state.selectedTeamMembers.length) {
      formData.myTeam = state.selectedTeamMembers.map((item) => item?.user_id);
    }

    if (showAccountDirectorField) {
      // Legacy-data grace: users saved before the AD list loads (or whose AD
      // left the AD role) may have an accountDirectorId the select can't
      // display. Only error when there's no AD anywhere; when the select is
      // blank but the user already has one, omit the key — Auth0's metadata
      // merge preserves the existing value.
      const effectiveAccountDirectorId =
        state.selectedAccountDirector?.value ||
        state.user?.app_metadata?.accountDirectorId;

      if (accountDirectorRequired && !effectiveAccountDirectorId) {
        return toast.error("Please select an Account Director.", toastOptions);
      }

      if (state.selectedAccountDirector?.value) {
        formData.accountDirectorId = state.selectedAccountDirector.value;
      }
    }

    // No roles in the payload: role changes via this form are intentionally
    // unsupported (the role select is disabled; the server discards roles).

    const token = await getAccessTokenSilently().catch((e) => {
      console.error(e);
      return loginWithRedirect();
    });

    dispatch({ type: "fieldChanged", field: "isSubmitting", value: true });

    try {
      await ai.auth(token).put(`/api/users/${id}`, formData);

      if (id === user?.sub) {
        // Self-edit: refresh the cached token so new claims apply immediately.
        await getAccessTokenSilently({
          ignoreCache: true,
        });
      }

      toast.success("User Updated Successfully!", toastOptions);
    } catch (error) {
      // Status only — the axios error carries the submitted form data.
      Sentry.captureMessage(
        `PUT /api/users/:id failed: ${error?.response?.status ?? "network"}`
      );

      toast.error(
        getApiErrorMessage(error, "Failed to update user."),
        toastOptions
      );
    } finally {
      dispatch({ type: "fieldChanged", field: "isSubmitting", value: false });
    }
  };

  return (
    <Fragment>
      <h2 id="addAirportHeading">Edit User</h2>
      <form id="" className="base-form two-column" onSubmit={handleSubmit}>
        <div className="form-group">
          <label htmlFor="firstName">First Name</label>
          <input
            type="text"
            name="firstName"
            value={state.firstName}
            placeholder="First Name"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="lastName">Last Name</label>
          <input
            type="text"
            name="lastName"
            value={state.lastName}
            placeholder="Last Name"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="phone">Phone Number</label>
          <input
            type="text"
            name="phone"
            value={state.phone}
            placeholder="Phone Number"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="phone">Email Address</label>
          <input
            type="email"
            name="email"
            value={state.email}
            placeholder="Email Address"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div>

        <div className="form-group">
          <label htmlFor="notificationEmails">Organization</label>

          <Select
            key={`organization-options-key-${JSON.stringify(
              state.selectedOrganization
            )}-${JSON.stringify(state.organizations)}`}
            className="organization-select"
            classNamePrefix="select"
            options={state.organizations}
            defaultValue={state.selectedOrganization}
            placeholder="Organization"
            onChange={(selectedOrganization) => {
              dispatch({
                type: "fieldChanged",
                field: "selectedOrganization",
                value: selectedOrganization,
              });
            }}
          ></Select>
        </div>

        {/* <div className="form-group">
          <label htmlFor="phone">Password</label>
          <input
            type="text"
            name="password"
            value={state.password}
            placeholder="Password"
            onChange={(e) =>
              dispatch({
                type: "fieldChanged",
                field: e.target.name,
                value: e.target.value,
              })
            }
          ></input>
        </div> */}

        <div className="form-group">
          <label htmlFor="userRoles">User Role</label>

          <Select
            key={`role-options-key-${JSON.stringify(
              state.selectedRole
            )}-${JSON.stringify(state.availableRoles)}`}
            name="userRoles"
            className="organization-select"
            classNamePrefix="select"
            options={state.availableRoles}
            defaultValue={state.selectedRole}
            placeholder="User Role"
            onChange={(selectedRole) => {
              dispatch({
                type: "fieldChanged",
                field: "selectedRole",
                value: selectedRole,
              });
            }}
            isDisabled={true}
          ></Select>
        </div>

        <div className="form-group mb-40">
          <label>User sub-role</label>


          <div>
            <Checkbox
              id="isSalesRep"
              name="isSalesRep"
              checked={state.isSalesRep}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) =>
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                })
              }
            />
            <label htmlFor="isSalesRep" className="mb-0 va-middle">Sales Rep</label>
          </div>

          <div>
            <Checkbox
              id="isOps"
              name="isOps"
              checked={state.isOps}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) =>
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                })
              }
            />
            <label htmlFor="isOps" className="mb-0 va-middle">OPS</label>
          </div>
          <div>
            <Checkbox
              id="isAccounting"
              name="isAccounting"
              checked={state.isAccounting}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) => {
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                });
                e.target.checked && dispatch({
                  type: "fieldChanged",
                  field: "isAccountingPending",
                  value: false,
                });
              }}
              disabled={state.isAccountingPending}
            />
            <label htmlFor="isAccounting" className="mb-0 va-middle">Accounting – Admin</label>
          </div>
          <div>
            <Checkbox
              id="isAccountingLedger"
              name="isAccountingLedger"
              checked={state.isAccountingLedger}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) => {
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                });
              }}
            />
            <label htmlFor="isAccountingLedger" className="mb-0 va-middle">Accounting – Ledger</label>
          </div>
          <div>
            <Checkbox
              id="isAccountingPending"
              name="isAccountingPending"
              checked={state.isAccountingPending}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) => {
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                });
                e.target.checked && dispatch({
                  type: "fieldChanged",
                  field: "isAccounting",
                  value: false,
                });
              }}
              disabled={state.isAccounting}
            />
            <label htmlFor="isAccountingPending" className="mb-0 va-middle">Accounting – Pending</label>
          </div>
          <div>
            <Checkbox
              id="isDutySales"
              name="isDutySales"
              checked={state.isDutySales}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) => {
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                });
              }}
            />
            <label htmlFor="isDutySales" className="mb-0 va-middle">Duty Status – Sales</label>
          </div>
          <div>
            <Checkbox
              id="isDutyAdmin"
              name="isDutyAdmin"
              checked={state.isDutyAdmin}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) => {
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                });
              }}
            />
            <label htmlFor="isDutyAdmin" className="mb-0 va-middle">Duty Status – Admin</label>
          </div>
          <div>
            <Checkbox
              id="isVerificationAdmin"
              name="isVerificationAdmin"
              checked={state.isVerificationAdmin}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) =>
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                })
              }
            />
            <label htmlFor="isVerificationAdmin" className="mb-0 va-middle">Verification Admin</label>
          </div>
          {/* Enforced server-side by contractActionPolicy; only a Super Admin can grant these. */}
          <div>
            <Checkbox
              id="isCcApprover"
              name="isCcApprover"
              checked={state.isCcApprover}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) =>
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                })
              }
            />
            <label htmlFor="isCcApprover" className="mb-0 va-middle">CC Approver</label>
          </div>
          <div>
            <Checkbox
              id="isMarkSentAllowed"
              name="isMarkSentAllowed"
              checked={state.isMarkSentAllowed}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) =>
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                })
              }
            />
            <label htmlFor="isMarkSentAllowed" className="mb-0 va-middle">Mark Sent (own contracts)</label>
          </div>
          <div>
            <Checkbox
              id="isHighDollarApprover"
              name="isHighDollarApprover"
              checked={state.isHighDollarApprover}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) =>
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                })
              }
            />
            <label htmlFor="isHighDollarApprover" className="mb-0 va-middle">High Dollar Approver (to $100k)</label>
          </div>
          <div>
            <Checkbox
              id="isHighDollarApprover100k"
              name="isHighDollarApprover100k"
              checked={state.isHighDollarApprover100k}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) =>
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                })
              }
            />
            <label htmlFor="isHighDollarApprover100k" className="mb-0 va-middle">High Dollar Approver ($100k+)</label>
          </div>
          <div>
            <Checkbox
              id="isMarkSentBlocked"
              name="isMarkSentBlocked"
              checked={state.isMarkSentBlocked}
              className="checkbox-evo-orange checkbox-aligned"
              onChange={(e) =>
                dispatch({
                  type: "fieldChanged",
                  field: e.target.name,
                  value: e.target.checked,
                })
              }
            />
            <label htmlFor="isMarkSentBlocked" className="mb-0 va-middle">Block Mark Sent</label>
          </div>
        </div>

        <div className="form-group"></div>

        {showAccountDirectorField && (
            <div className="form-group">
              <label htmlFor="accountDirector">
                Account Director{accountDirectorRequired ? "" : " (optional)"}
              </label>

              <Select
                key={`account-director-options-key-${JSON.stringify(
                  state.availableAccountDirectors
                )}`}
                name="accountDirector"
                className="base-select"
                classNamePrefix="select"
                options={state.availableAccountDirectors}
                value={state.selectedAccountDirector}
                placeholder="Account Director"
                onChange={(selectedAD) => {
                  dispatch({
                    type: "fieldChanged",
                    field: "selectedAccountDirector",
                    value: selectedAD,
                  });
                }}
              ></Select>
            </div>
          )}

        {isEvoOrg && isAccountDirector && (
            <>
              <div className="form-group">
                <label htmlFor="teamMembers">Team Members</label>

                <Select
                  key={`account-director-options-key-${JSON.stringify(
                    state.selectedTeamMembers
                  )}`}
                  name="teamMembers"
                  className="base-select"
                  classNamePrefix="select"
                  options={state.availableTeamMembers}
                  placeholder="Team Members"
                  defaultValue={state.selectedTeamMembers}
                  onChange={(selectedTeamMembers) => {
                    dispatch({
                      type: "fieldChanged",
                      field: "selectedTeamMembers",
                      value: selectedTeamMembers,
                    });
                  }}
                  isMulti
                ></Select>
              </div>

              <div className="form-group">
                <label htmlFor="teamColor">Team Color (optional)</label>
                <Select
                  name="teamColor"
                  className="base-select"
                  classNamePrefix="select"
                  options={TEAM_COLOR_OPTIONS}
                  value={
                    TEAM_COLOR_OPTIONS.find(
                      (option) => option.value === (state.teamColor || "")
                    ) || null
                  }
                  placeholder="Team Color"
                  onChange={(selectedColor) =>
                    dispatch({
                      type: "fieldChanged",
                      field: "teamColor",
                      value: selectedColor?.value ?? "",
                    })
                  }
                ></Select>
              </div>
            </>
          )}

        {/* {state.selectedOrganization?.value === "600c8d371f10c61723e64402" && (
          <div className="form-group">
            <label htmlFor="outreachUserId">Outreach User</label>

            <Select
              key={`account-director-options-key-${JSON.stringify(
                state.availableOutreachUsers
              )}`}
              name="outreachUserId"
              className="base-select"
              classNamePrefix="select"
              options={state.availableOutreachUsers}
              placeholder="Outreach User"
              onChange={(selectedOutreachUser) => {
                dispatch({
                  type: "fieldChanged",
                  field: "selectedOutreachUser",
                  value: selectedOutreachUser,
                });
              }}
            ></Select>
          </div>
        )} */}

        <input
          type="submit"
          value={state.isSubmitting ? "Saving..." : "Save User"}
          disabled={state.isSubmitting}
        ></input>
      </form>

      {/* <pre>{JSON.stringify(state.selectedAccountDirector, null, 2)}</pre> */}
    </Fragment>
  );
};

export default SingleUserContent;
